import { action } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { TxSerialized, type TxSigned } from '@root/entities/transaction'
import { WalletId } from '@root/entities/wallet'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { UnknownError } from '@root/shared'
import { ApiError, apiRequest } from '@root/shared/api'

import type { TypeHelper } from '@blade/std-lib'
import type {
  DistributionConfirmCreatePayload,
  DistributionPrepareCreatePayload,
} from '@blade/trade-api-client'
import type { CurrencyAddress } from '@root/entities/currency'

type Args = TypeHelper.OverrideKeys<
  DistributionPrepareCreatePayload,
  {
    network: Network.Network
    sourceWalletId: WalletId
    tokenAddress: CurrencyAddress
  }
>

export const prepareDistributeFundsRequestAction = action(
  async (ctx, { network, ...req }: Args) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.distribution.distributionPrepareCreate, [
        {
          network: Network.toDto(network),
          ...req,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'prepareDistributeFundsRequestAction',
)

type WalletTransaction = TypeHelper.OverrideKeys<
  DistributionConfirmCreatePayload['signedTransactions'][number],
  { signedTx: TxSigned; walletIds: Array<WalletId> }
>

export const confirmDistributeFundsRequestAction = action(
  async (
    ctx,
    {
      network,
      ...req
    }: Args & { signedTransactions: Array<WalletTransaction> },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.distribution.distributionConfirmCreate, [
        {
          network: Network.toDto(network),
          ...req,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'confirmDistributeFundsRequestAction',
)

export const processDistributeFundsAction = action(async (ctx, req: Args) => {
  const prepareResult = await ctx.schedule(() =>
    prepareDistributeFundsRequestAction(ctx, {
      ...req,
    }),
  )
  if (Result.isFailure(prepareResult)) {
    return Result.fail(prepareResult.failure)
  }

  const { secureStorageService } = ctx.get(dependenciesAtom)

  const tx = prepareResult.success.transactions.at(0)
  if (!tx) {
    return Result.fail(UnknownError.fromUnknown('No transaction found'))
  }

  const signedResult = await ctx.schedule(() =>
    secureStorageService.signPlainTransaction({
      network: req.network,
      walletId: req.sourceWalletId,
      tx: TxSerialized(tx.transaction),
    }),
  )

  if (Result.isFailure(signedResult)) {
    return Result.fail(UnknownError.fromUnknown(signedResult.failure))
  }

  const sendResult = await ctx.schedule(() =>
    confirmDistributeFundsRequestAction(ctx, {
      network: req.network,
      signedTransactions: [
        {
          amount: Number(tx.amount),
          tokenAddress: req.tokenAddress,
          signedTx: signedResult.success.txSigned,
          walletIds: tx.walletIds.map((id) => WalletId(id)),
        },
      ],
      sourceWalletId: req.sourceWalletId,
      tokenAddress: req.tokenAddress,
      distributionItems: [],
    }),
  )

  return sendResult
}, 'processDistributeFundsAction')
